import { Avatar, Box, Portal } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { Icon, IconButton, Typography } from '@sm-dashboard/ui-components';
import {
  CONTACT_US_NUMBER,
  isWorldShalichUser,
  incomesDashboardRoute,
  incomesRoute,
  pagesList,
  standingOrdersRoute,
  worldShalich
} from '@/constants';
import { useState, useEffect, useMemo, cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Page } from '@sm-dashboard/types';
import { useAppContext } from '@/context/context';
import { usePaymentConfig } from '@/hooks/usePaymentConfig';
import LayoutTheme from './HoverTheme';
import {
  MOBILE_BOTTOM_NAV_INSET,
  MOBILE_NAV_BOTTOM,
  MOBILE_NAV_HEIGHT,
  SAFE_AREA_BOTTOM,
  SAFE_AREA_TOP
} from './mobileLayoutConstants';
import { getUser } from '@/util/getUser';
import chabadLogo from '@/assets/chabad-logo.png';
import chabadLogoWorld from '@/assets/chabad-logo-world.png';
import defaultAvatar from '@/assets/avatar.png';
import defaultAvatarW from '@/assets/avatar-w.png';

const financeLinks = new Set([incomesDashboardRoute, incomesRoute, standingOrdersRoute]);

const MOBILE_MENU_ITEM_BG = '#FFFFFF2E';
const MOBILE_MENU_ITEM_RADIUS = '20px';
const MOBILE_MENU_ITEM_HEIGHT = 90;
const MOBILE_MENU_SUB_ITEM_HEIGHT = 52;
const MOBILE_MENU_SUB_ITEM_RADIUS = '10px';
const MOBILE_MENU_SUB_ICON_WRAPPER_RADIUS = '5px';
const MOBILE_MENU_SUB_ICON_WRAPPER_SIZE = 39;
const MOBILE_MENU_SUB_ICON_SIZE = 22;
const MOBILE_MENU_SECTION_HEADER_ICON_SIZE = 25;
const MOBILE_MENU_ICON_SIZE = 27;

const mobileMenuSectionHeaderIconSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  lineHeight: 0,
  color: 'common.white',
  backgroundColor: 'transparent',
  '& .MuiSvgIcon-root': {
    fontSize: MOBILE_MENU_SECTION_HEADER_ICON_SIZE,
    color: 'common.white'
  },
  '& .SmIcon-root': {
    fontSize: MOBILE_MENU_SECTION_HEADER_ICON_SIZE,
    color: 'common.white'
  }
} as const;

function renderMobileMenuHeaderIcon(icon: Page['icon']) {
  if (!isValidElement(icon)) {
    return icon;
  }

  const iconElement = icon as ReactElement<{ icon?: string; color?: string; sx?: object }>;
  const { icon: iconName, sx } = iconElement.props;

  if (typeof iconName === 'string') {
    return cloneElement(iconElement, { color: 'common.white' });
  }

  return cloneElement(iconElement, {
    color: 'inherit',
    sx: {
      ...(sx ?? {}),
      fontSize: MOBILE_MENU_SECTION_HEADER_ICON_SIZE,
      color: 'common.white'
    }
  });
}

const mobileMenuItemIconWrapperSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 48,
  height: 48,
  borderRadius: '12px',
  backgroundImage:
    'linear-gradient(0deg, rgba(255, 217, 217, 0.3), rgba(255, 217, 217, 0.3)), linear-gradient(0deg, #6B1224, #6B1224)',
  color: 'common.white',
  '& .MuiSvgIcon-root': {
    fontSize: MOBILE_MENU_ICON_SIZE,
    width: MOBILE_MENU_ICON_SIZE,
    height: MOBILE_MENU_ICON_SIZE,
    color: (theme: Theme) => `${theme.palette.common.white} !important`
  },
  '& .SmIcon-root': {
    fontSize: MOBILE_MENU_ICON_SIZE,
    width: MOBILE_MENU_ICON_SIZE,
    height: MOBILE_MENU_ICON_SIZE
  },
  '& svg': {
    width: MOBILE_MENU_ICON_SIZE,
    height: MOBILE_MENU_ICON_SIZE
  }
} as const;

const MOBILE_MENU_CHEVRON_SIZE = 26;
const MOBILE_MENU_SUBTITLE_PADDING = 30;

const mobileMenuChevronWrapperSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  lineHeight: 0,
  '& .SmIcon-root': {
    fontSize: MOBILE_MENU_CHEVRON_SIZE
  }
} as const;

const mobileMenuHeaderTitleRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minWidth: 0,
  flex: 1
} as const;

const mobileMenuHeaderTitleSx = {
  fontSize: '20.8px',
  fontWeight: 700,
  color: 'common.white',
  lineHeight: 1,
  m: 0,
  display: 'flex',
  alignItems: 'center'
} as const;

const mobileMenuHeaderIconButtonSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  border: 'none',
  p: 0,
  m: 0,
  lineHeight: 0,
  backgroundColor: 'transparent',
  cursor: 'pointer',
  color: 'common.white'
} as const;

const mobileMenuItemButtonSx = {
  width: '100%',
  height: MOBILE_MENU_ITEM_HEIGHT,
  minHeight: MOBILE_MENU_ITEM_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  px: 2,
  py: 0,
  border: 'none',
  borderRadius: MOBILE_MENU_ITEM_RADIUS,
  backgroundColor: MOBILE_MENU_ITEM_BG,
  color: 'common.white',
  cursor: 'pointer',
  textAlign: 'start',
  boxSizing: 'border-box'
} as const;

const mobileMenuSubItemButtonSx = {
  width: '100%',
  height: MOBILE_MENU_SUB_ITEM_HEIGHT,
  minHeight: MOBILE_MENU_SUB_ITEM_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  py: 0,
  paddingInlineStart: '6px',
  paddingInlineEnd: 2,
  border: 'none',
  borderRadius: MOBILE_MENU_SUB_ITEM_RADIUS,
  backgroundColor: MOBILE_MENU_ITEM_BG,
  color: 'common.white',
  cursor: 'pointer',
  textAlign: 'start',
  boxSizing: 'border-box'
} as const;

const mobileMenuSubItemIconWrapperSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: MOBILE_MENU_SUB_ICON_WRAPPER_SIZE,
  height: MOBILE_MENU_SUB_ICON_WRAPPER_SIZE,
  borderRadius: MOBILE_MENU_SUB_ICON_WRAPPER_RADIUS,
  backgroundImage:
    'linear-gradient(0deg, rgba(255, 217, 217, 0.3), rgba(255, 217, 217, 0.3)), linear-gradient(0deg, #6B1224, #6B1224)',
  color: 'primary.main',
  '& .MuiSvgIcon-root': {
    fontSize: MOBILE_MENU_SUB_ICON_SIZE,
    width: MOBILE_MENU_SUB_ICON_SIZE,
    height: MOBILE_MENU_SUB_ICON_SIZE,
    color: (theme: Theme) => `${theme.palette.common.white} !important`
  },
  '& .SmIcon-root': {
    fontSize: MOBILE_MENU_SUB_ICON_SIZE,
    width: MOBILE_MENU_SUB_ICON_SIZE,
    height: MOBILE_MENU_SUB_ICON_SIZE
  },
  '& svg': {
    width: MOBILE_MENU_SUB_ICON_SIZE,
    height: MOBILE_MENU_SUB_ICON_SIZE
  }
} as const;

type MobileMenuItemLabelsProps = {
  title: string;
  subtitle?: string;
  variant?: 'main' | 'child';
};

const MobileMenuItemLabels = ({ title, subtitle, variant = 'main' }: MobileMenuItemLabelsProps) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.25,
      textAlign: 'start'
    }}
  >
    {variant === 'main' ? (
      <Box
        sx={{
          fontSize: '20px',
          fontWeight: 700,
          lineHeight: 1.3,
          color: 'common.white',
          display: 'block'
        }}
      >
        {title}
      </Box>
    ) : (
      <Box
        component="span"
        sx={{
          fontSize: 18,
          fontWeight: 500,
          lineHeight: 1.3,
          color: 'common.white'
        }}
      >
        {title}
      </Box>
    )}
    {subtitle ? (
      <Box
        component="span"
        sx={{
          fontSize: 14,
          lineHeight: 1.3,
          color: 'common.white',
          opacity: 0.9
        }}
      >
        {subtitle}
      </Box>
    ) : null}
  </Box>
);

const MOBILE_MENU_NAV_VALUE = '__menu__';

const normalizeNavPath = (path: string) => {
  const normalized = path.endsWith('/') ? path.slice(0, -1).toLowerCase() : path.toLowerCase();
  return normalized || '/';
};

const isRouteMatch = (routeLink: string | undefined, currentPath: string) => {
  if (!routeLink) return false;
  const normalizedRoute = normalizeNavPath(routeLink);
  return currentPath === normalizedRoute || (normalizedRoute !== '/' && currentPath.startsWith(`${normalizedRoute}/`));
};

function renderBottomNavIcon(icon: ReactNode, isActive: boolean) {
  if (!isActive || !isValidElement(icon)) {
    return icon;
  }

  const iconElement = icon as ReactElement<{ icon?: string; color?: string; sx?: object }>;
  if (typeof iconElement.props.icon === 'string') {
    return cloneElement(iconElement, { color: 'common.white' });
  }

  return cloneElement(iconElement, {
    color: 'inherit',
    sx: {
      ...(iconElement.props.sx ?? {}),
      color: 'common.white'
    }
  });
}
const MOBILE_MENU_OVERLAY_BLUR = '28px';
const MOBILE_MENU_BLUR_BACKDROP_WHITE_ALPHA = 0.18;
const MOBILE_MENU_RED_TINT_COLOR = 'rgb(85, 11, 31)';
const MOBILE_MENU_RED_TINT_OPACITY = 0.82;

const mobileMenuOverlayShellSx = {
  position: 'fixed' as const,
  top: 0,
  left: 0,
  right: 0,
  // Prefer measured app height so the red shell fills the WebView edge-to-edge
  // (bottom: 0 alone can leave letterbox gaps when visualViewport is shorter).
  height: 'var(--app-height, 100vh)',
  zIndex: 1000,
  overflow: 'hidden' as const,
  // Solid fill so safe-area / letterbox regions never show white behind the menu.
  backgroundColor: MOBILE_MENU_RED_TINT_COLOR,
  margin: 0,
  padding: 0
};

const mobileMenuBlurBackdropSx = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 0,
  pointerEvents: 'none' as const,
  backgroundColor: `rgba(255, 255, 255, ${MOBILE_MENU_BLUR_BACKDROP_WHITE_ALPHA})`,
  backdropFilter: `blur(${MOBILE_MENU_OVERLAY_BLUR})`,
  WebkitBackdropFilter: `blur(${MOBILE_MENU_OVERLAY_BLUR})`
};

const mobileMenuRedTintSx = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1,
  pointerEvents: 'none' as const,
  backgroundColor: MOBILE_MENU_RED_TINT_COLOR,
  opacity: MOBILE_MENU_RED_TINT_OPACITY
};

const mobileMenuScrollSx = {
  position: 'relative' as const,
  zIndex: 2,
  height: '100%',
  overflowY: 'auto' as const,
  boxSizing: 'border-box' as const,
  pt: SAFE_AREA_TOP,
  pb: SAFE_AREA_BOTTOM,
  '&::-webkit-scrollbar': {
    display: 'none'
  },
  scrollbarWidth: 'none',
  msOverflowStyle: 'none'
};
const PROFILE_ROUTE = '/system/shluchim/profile';

const getShaliachDisplayName = (user: ReturnType<typeof getUser>) => {
  if (user?.nickName) {
    return user.nickName;
  }

  const firstName = user?.firstName || '';
  const lastName = user?.lastName || '';

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }

  return firstName || lastName || 'משתמש';
};

type MobileMenuUserRowProps = {
  onNavigateToProfile: () => void;
};

const MobileMenuUserRow = ({ onNavigateToProfile }: MobileMenuUserRowProps) => {
  const user = getUser();
  const { shaliach, previewProfile, userPermission } = useAppContext();
  const currentUser = shaliach || user;
  const fullName = getShaliachDisplayName(currentUser);
  const branchName = currentUser?.Branch?.branchName || 'ללא סניף';
  const avatarSrc =
    previewProfile ||
    currentUser?.profileImage ||
    (currentUser?.tags?.includes('שליחה') ? defaultAvatarW : defaultAvatar);
  const logoSrc = userPermission?.hasTag(worldShalich) ? chabadLogoWorld : chabadLogo;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        px: 2,
        pb: 2.5,
        flexShrink: 0
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
        <Avatar
          alt={fullName}
          src={avatarSrc}
          sx={{
            width: 48,
            height: 48,
            flexShrink: 0,
            border: '2px solid rgba(255, 255, 255, 0.35)'
          }}
        >
          {((currentUser?.firstName || '').charAt(0) + (currentUser?.lastName || '').charAt(0)).toUpperCase() ||
            (currentUser?.nickName || '').charAt(0).toUpperCase() ||
            'מל'}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <IconButton variant="text" aria-label="עריכת פרופיל" size="small" onClick={onNavigateToProfile}>
              <EditIcon sx={{ fontSize: 18, color: 'common.white' }} />
            </IconButton>
            <Box
              component="span"
              sx={{
                color: 'common.white',
                fontWeight: 600,
                fontSize: 16,
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {fullName}
            </Box>
          </Box>
          <Box
            component="span"
            sx={{
              color: 'common.white',
              opacity: 0.85,
              fontSize: 14,
              lineHeight: 1.3,
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {branchName}
          </Box>
        </Box>
      </Box>
      <Box
        component="img"
        src={logoSrc}
        alt="Chabad"
        sx={{ width: 130, height: 'auto', flexShrink: 0, objectFit: 'contain' }}
      />
    </Box>
  );
};

const mobileNavGlassSx = {
  position: 'fixed' as const,
  bottom: MOBILE_NAV_BOTTOM,
  left: 0,
  right: 0,
  marginLeft: 'auto',
  marginRight: 'auto',
  width: 'min(390px, calc(100vw - 24px))',
  maxWidth: 'min(390px, calc(100vw - 24px))',
  boxSizing: 'border-box' as const,
  zIndex: 1100,
  height: MOBILE_NAV_HEIGHT,
  borderRadius: '100px',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 0.75,
  px: 1.25,
  pt: '10px',
  pb: '10px',
  backgroundColor: 'transparent',
  backgroundImage:
    'linear-gradient(0deg, rgba(175, 189, 210, 0.2), rgba(175, 189, 210, 0.2)), linear-gradient(0deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.2))',
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
  pointerEvents: 'auto' as const
};

type ExtendedPage = Page & {
  isHeader?: boolean;
  isKedmaHeader?: boolean;
  isMessengersHeader?: boolean;
  isPersonalHeader?: boolean;
  isFinanceHeader?: boolean;
};

type BottomNavItem =
  | { primary: string; link: string; isMenuToggle: true }
  | { primary: string; link: string; icon: ReactNode };

// Mobile chrome only (bottom nav + full-screen menu overlay). The page content is
// rendered by DashboardLayout in a single element tree shared with desktop, so that
// crossing the md breakpoint doesn't unmount the page (forms keep their state).
const MobileDashboardChrome = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeMenuSectionIdx, setActiveMenuSectionIdx] = useState<number | null>(null);

  const { userPermission, setUser, shaliach, user } = useAppContext();
  const shaliachId = shaliach?.id ?? user?.id;
  const { hasPaymentConfig, configuredProviderType, loading: paymentConfigLoading } = usePaymentConfig(shaliachId);
  const showFinance = Boolean(hasPaymentConfig && configuredProviderType === 'cardcom');
  const hideFinance = Boolean(shaliachId) && !paymentConfigLoading && !showFinance;
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = normalizeNavPath(location.pathname);
  const isMobileNavReady = Boolean(userPermission);
  const navPages = pagesList;
  const hideChabadIsraelLogo = isWorldShalichUser(userPermission);

  useEffect(() => {
    if (!isMobileNavReady) {
      setIsMenuOpen(false);
      setActiveMenuSectionIdx(null);
    }
  }, [isMobileNavReady]);

  const closeMenu = () => {
    setIsMenuOpen(false);
    setActiveMenuSectionIdx(null);
  };

  const isMenuChildVisible = (child: Page) => {
    if (child.isFinanceHeader || financeLinks.has(child.link || '')) {
      return !hideFinance;
    }
    if (child.isHeader || child.isKedmaHeader || child.isMessengersHeader || child.isPersonalHeader) {
      return true;
    }
    if (!child.primary) {
      return false;
    }
    if (!userPermission) {
      return false;
    }
    if (userPermission.isAdmin) {
      return true;
    }
    return Boolean(child.menuAction || userPermission.hasPagePermission(child.link || ''));
  };

  const handleMenuChildClick = (child: ExtendedPage) => {
    if (child.menuAction === 'contact') {
      window.open(`https://wa.me/${CONTACT_US_NUMBER}`, '_blank');
      closeMenu();
      return;
    }

    if (child.menuAction === 'logout') {
      window.localStorage.removeItem('token');
      setUser(undefined);
      navigate('/login');
      closeMenu();
      return;
    }

    if (child.link) {
      navigate(child.link);
      closeMenu();
    }
  };

  const renderMenuChildItem = (child: ExtendedPage, childIndex: number) => {
    if (child.isHeader) {
      if (hideChabadIsraelLogo) return null;
      return (
        <Box key={childIndex} sx={{ px: 1, py: 1 }}>
          <LayoutTheme type="logo" />
        </Box>
      );
    }
    if (child.isKedmaHeader) {
      return (
        <Box key={childIndex} sx={{ px: 1, py: 1 }}>
          <LayoutTheme type="kedma" />
        </Box>
      );
    }
    if (child.isMessengersHeader) {
      return (
        <Box key={childIndex} sx={{ px: 1, py: 1 }}>
          <LayoutTheme type="messengers" />
        </Box>
      );
    }
    if (child.isPersonalHeader) {
      return (
        <Box key={childIndex} sx={{ px: 1, py: 1 }}>
          <LayoutTheme type="personal" />
        </Box>
      );
    }
    if (child.isFinanceHeader) {
      if (hideFinance) return null;
      return (
        <Box key={childIndex} sx={{ px: 1, py: 1 }}>
          <LayoutTheme type="finance" />
        </Box>
      );
    }

    if (!child.primary || !isMenuChildVisible(child)) {
      return null;
    }

    const isChildActive = isRouteMatch(child.link, currentPath);

    return (
      <Box
        key={childIndex}
        component="button"
        type="button"
        aria-current={isChildActive ? 'page' : undefined}
        onClick={() => handleMenuChildClick(child)}
        sx={mobileMenuSubItemButtonSx}
      >
        <Box
          sx={{
            ...mobileMenuSubItemIconWrapperSx,
            ...(isChildActive
              ? {
                  backgroundColor: 'primary.main',
                  color: 'common.white',
                  '& .MuiSvgIcon-root': {
                    color: 'common.white !important'
                  },
                  '& .SmIcon-root': {
                    color: 'common.white !important'
                  },
                  '& svg': {
                    color: 'common.white !important'
                  },
                  '& svg, & svg path': {
                    fill: 'common.white !important',
                    stroke: 'common.white !important'
                  }
                }
              : {})
          }}
        >
          {renderBottomNavIcon(child.icon, isChildActive)}
        </Box>
        <MobileMenuItemLabels title={child.primary} subtitle={child.mobileMenuSubtitle} variant="child" />
      </Box>
    );
  };

  const filteredPagesList = navPages.filter(page => {
    if (!userPermission) {
      return false;
    }

    if (userPermission.isAdmin) {
      return true;
    }

    const hasPagePermission = (link: string) => userPermission.hasPagePermission(link);
    const isChildVisible = (child: Page) => Boolean(child.menuAction || hasPagePermission(child.link || ''));
    const isItemVisible = (navPage: Page) => {
      const hasChildren = navPage.children && navPage.children.length > 0;
      if (!hasChildren) {
        return hasPagePermission(navPage.link || '');
      }
      return navPage.children!.some(isChildVisible);
    };

    const hasOtherVisibleNavItems = navPages.some(navItem => !navItem.isAccountMenu && isItemVisible(navItem));

    if (page.isAccountMenu) {
      return hasOtherVisibleNavItems && isItemVisible(page);
    }

    return isItemVisible(page);
  });

  // Mobile pages - נוצר מ-pagesList של הפרונטאנד הראשי
  const flattenedPagesList = filteredPagesList.reduce((acc: Page[], page) => {
    acc.push(page);
    if (page.children) {
      page.children.forEach(child => {
        acc.push(child);
      });
    }
    return acc;
  }, []);

  const mobilePagesList = flattenedPagesList
    .filter(page => page.isMobilePrimary)
    .slice(0, 4)
    .map(page => ({
      primary: page.mobilePrimaryLabel ?? page.primary,
      link: page.link || page.children?.[0]?.link || '',
      icon: page.icon
    }));

  const bottomNavItems: BottomNavItem[] = [
    {
      primary: 'תפריט',
      link: MOBILE_MENU_NAV_VALUE,
      isMenuToggle: true
    },
    ...mobilePagesList
  ];

  const bottomNav = (
    <Box component="nav" aria-label="ניווט ראשי" sx={mobileNavGlassSx}>
      {bottomNavItems.map((item, index) => {
        const isMenuToggle = 'isMenuToggle' in item && item.isMenuToggle;
        const isNavActive = !isMenuToggle && isRouteMatch(item.link, currentPath);
        const isIconActive = isMenuToggle ? isMenuOpen : isNavActive;

        return (
          <Box
            key={index}
            component="button"
            type="button"
            aria-current={isNavActive ? 'page' : undefined}
            aria-expanded={isMenuToggle ? isMenuOpen : undefined}
            aria-label={isMenuToggle ? 'תפריט' : item.primary}
            onClick={() => {
              if (isMenuToggle) {
                const nextOpen = !isMenuOpen;
                setIsMenuOpen(nextOpen);
                if (!nextOpen) {
                  setActiveMenuSectionIdx(null);
                }
                return;
              }

              navigate(item.link);
              setIsMenuOpen(false);
              setActiveMenuSectionIdx(null);
            }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              border: 'none',
              p: 0,
              minWidth: 0,
              flex: 1,
              backgroundColor: 'transparent',
              cursor: 'pointer',
              opacity: isIconActive ? 1 : 0.92
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 38,
                height: 38,
                border: '1px solid #FFFFFF33',
                borderRadius: '10px',
                backgroundColor: isIconActive ? 'primary.main' : '#FFFFFF99',
                flexShrink: 0,
                '& .MuiSvgIcon-root, & .SmIcon-root': {
                  fontSize: 19,
                  width: 19,
                  height: 19,
                  color: isIconActive ? 'common.white' : 'primary.main'
                }
              }}
            >
              {isMenuToggle ? (
                isMenuOpen ? (
                  <CloseIcon sx={{ fontSize: 19, color: 'common.white' }} />
                ) : (
                  <Icon icon="menu" color="primary.main" size="medium" />
                )
              ) : 'icon' in item ? (
                renderBottomNavIcon(item.icon, isNavActive)
              ) : null}
            </Box>
            {item.primary ? (
              <Typography variant="mobileNavLabel" color={isMenuOpen ? 'common.white' : 'common.black'}>
                {item.primary}
              </Typography>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );

  const activeMenuSection = activeMenuSectionIdx !== null ? filteredPagesList[activeMenuSectionIdx] : null;

  const mobileChrome = (
    <>
      {isMobileNavReady && isMenuOpen ? (
        <Box sx={mobileMenuOverlayShellSx}>
          <Box aria-hidden sx={mobileMenuBlurBackdropSx} />
          <Box aria-hidden sx={mobileMenuRedTintSx} />
          <Box sx={mobileMenuScrollSx}>
            <Box
              sx={{
                px: 2,
                pt: 2,
                pb: 2.5,
                flexShrink: 0
              }}
            >
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1
                  }}
                >
                  <Box sx={mobileMenuHeaderTitleRowSx}>
                    {activeMenuSection ? (
                      <Box
                        component="button"
                        type="button"
                        aria-label="חזרה לתפריט הראשי"
                        onClick={() => setActiveMenuSectionIdx(null)}
                        sx={mobileMenuHeaderIconButtonSx}
                      >
                        <Box sx={mobileMenuChevronWrapperSx}>
                          <Icon icon="chevronRight" color="common.white" size="medium" />
                        </Box>
                      </Box>
                    ) : null}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'end',
                        gap: 1,
                        flexWrap: 'wrap',
                        lineHeight: 1,
                        flex: 1,
                        minWidth: 0
                      }}
                    >
                      <Box sx={mobileMenuHeaderTitleSx}>
                        {activeMenuSection ? activeMenuSection.primary.trim() : 'תפריט'}
                      </Box>
                      {activeMenuSection ? (
                        <Box sx={mobileMenuSectionHeaderIconSx}>
                          {renderMobileMenuHeaderIcon(activeMenuSection.icon)}
                        </Box>
                      ) : null}
                    </Box>
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    aria-label="סגור תפריט"
                    onClick={closeMenu}
                    sx={{
                      ...mobileMenuHeaderIconButtonSx,
                      lineHeight: 0
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 30, width: 30, height: 30, display: 'block' }} />
                  </Box>
                </Box>
                {activeMenuSection?.mobileMenuSubtitle ? (
                  <Box
                    component="p"
                    sx={{
                      m: 0,
                      mt: 0.5,
                      fontSize: 14,
                      lineHeight: 1.3,
                      color: 'common.white',
                      opacity: 0.9,
                      paddingInlineStart: `${MOBILE_MENU_SUBTITLE_PADDING}px`
                    }}
                  >
                    {activeMenuSection.mobileMenuSubtitle}
                  </Box>
                ) : null}
              </Box>
            </Box>
            {activeMenuSectionIdx === null ? (
              <>
                <MobileMenuUserRow
                  onNavigateToProfile={() => {
                    navigate(PROFILE_ROUTE);
                    closeMenu();
                  }}
                />
                <Box sx={{ px: 2, pb: 2.5 }}>
                  <Box sx={{ borderBottom: '2px solid #FFFFFF2E' }} />
                </Box>
              </>
            ) : null}
            <Box
              sx={{
                px: 2,
                pt: 2,
                pb: isMobileNavReady ? MOBILE_BOTTOM_NAV_INSET : undefined,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                alignItems: 'stretch'
              }}
            >
              {activeMenuSectionIdx === null
                ? filteredPagesList.map((page, pageIdx) => {
                    const hasChildren = Boolean(page.children?.length);

                    return (
                      <Box
                        key={pageIdx}
                        component="button"
                        type="button"
                        onClick={() => {
                          if (!hasChildren && page.link) {
                            navigate(page.link);
                            closeMenu();
                            return;
                          }

                          if (hasChildren) {
                            setActiveMenuSectionIdx(pageIdx);
                          }
                        }}
                        sx={mobileMenuItemButtonSx}
                      >
                        <Box sx={mobileMenuItemIconWrapperSx}>{page.icon}</Box>
                        <MobileMenuItemLabels title={page.primary} subtitle={page.mobileMenuSubtitle} />
                        {hasChildren ? (
                          <Box sx={mobileMenuChevronWrapperSx}>
                            <Icon icon="chevronLeft" color="common.white" size="medium" />
                          </Box>
                        ) : null}
                      </Box>
                    );
                  })
                : filteredPagesList[activeMenuSectionIdx]?.children?.map((child, childIndex) =>
                    renderMenuChildItem(child as ExtendedPage, childIndex)
                  )}
            </Box>
          </Box>
        </Box>
      ) : null}

      {isMobileNavReady ? bottomNav : null}
    </>
  );

  return <Portal>{mobileChrome}</Portal>;
};

export default MobileDashboardChrome;
